import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { getCurrentSchool, ASSETS } from '../config/schools';

function SplashScreen({ onComplete }) {
  const [stage, setStage] = useState(0);
  const [flash, setFlash] = useState(false);
  const [shake, setShake] = useState(false);
  const school = getCurrentSchool();
  
  // En mode démo (pas de logo école), on fait une animation simple
  const isDemoMode = !school.showSchoolLogo;
  
  // Positions fixes pour les étoiles (évite le re-render aléatoire)
  const starPositions = useMemo(() => 
    [...Array(15)].map(() => ({
      left: `${Math.random() * 100}%`,
      top: `${Math.random() * 100}%`,
    })), []
  );

  useEffect(() => {
    let timeline;
    
    if (isDemoMode) {
      // Animation pour mode démo - logo DailyGlobe puis message
      timeline = [
        { delay: 300, action: () => setStage(1) },   // Logo DailyGlobe apparaît
        { delay: 3000, action: () => setStage(3) },  // Message final
        { delay: 5500, action: () => onComplete() }
      ];
    } else {
      // Animation complète avec collision pour écoles
      timeline = [
        { delay: 300, action: () => setStage(1) },
        { delay: 3000, action: () => setStage(2) },
        { delay: 4000, action: () => { setFlash(true); setShake(true); } },
        { delay: 4100, action: () => setFlash(false) },
        { delay: 4200, action: () => setFlash(true) },
        { delay: 4300, action: () => { setFlash(false); setShake(false); } },
        { delay: 4400, action: () => setFlash(true) },
        { delay: 4500, action: () => setFlash(false) },
        { delay: 5000, action: () => setStage(3) },
        { delay: 7500, action: () => onComplete() }
      ];
    }

    const timers = timeline.map(({ delay, action }) => 
      setTimeout(action, delay)
    );

    return () => timers.forEach(clearTimeout);
  }, [onComplete, isDemoMode]);

  const flashColors = ['#FFB703', '#E63946', '#FFFFFF', '#FB8500'];
  const currentFlashColor = flashColors[Math.floor(Math.random() * flashColors.length)];

  return (
    <div 
      className={`fixed inset-0 z-50 flex items-center justify-center overflow-hidden ${shake ? 'animate-shake' : ''}`}
      style={{ 
        background: flash ? currentFlashColor : 'linear-gradient(135deg, #FFB703 0%, #FB8500 50%, #E63946 100%)' 
      }}
    >
      {/* Halftone background */}
      <div className="absolute inset-0 halftone-bg opacity-20"></div>

      <AnimatePresence mode="wait">
        {/* Stage 1: DailyGlobe logo en mode démo - SANS cape */}
        {stage >= 1 && stage < 3 && isDemoMode && (
          <motion.div
            key="dailyglobe-demo"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="absolute z-20 flex flex-col items-center"
          >
            <motion.img 
              src={ASSETS.DAILYGLOBE_LOGO}
              alt="DailyGlobe" 
              className="w-64 h-64 md:w-80 md:h-80 object-contain"
              style={{ filter: 'drop-shadow(0 0 40px rgba(255, 255, 255, 0.6))' }}
              animate={{ 
                scale: [1, 1.05, 1],
                rotate: [0, 2, -2, 0]
              }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            />
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.6 }}
              className="text-3xl md:text-5xl mt-4 text-center"
              style={{
                fontFamily: 'Bangers, cursive',
                color: 'white',
                textShadow: '4px 4px 0px rgba(0,0,0,0.5)',
                letterSpacing: '3px'
              }}
            >
              DAILYGLOBE SCHOOL
            </motion.h2>
          </motion.div>
        )}
        
        {/* Stage 1-2: Logo DailyGlobe qui vole avec cape (mode école seulement) */}
        {stage >= 1 && stage < 2 && !isDemoMode && (
          <motion.div
            key="dailyglobe-fly"
            initial={{ x: -1200, y: -600, scale: 0.3, rotate: -30 }}
            animate={{ x: 0, y: 0, scale: 1, rotate: 0 }}
            exit={{ x: 600, y: -400, scale: 0.4, rotate: 30, opacity: 0 }}
            transition={{ duration: 2, ease: [0.34, 1.56, 0.64, 1] }}
            className="absolute z-20"
          >
            <motion.img 
              src={ASSETS.DAILYGLOBE_LOGO}
              alt="DailyGlobe" 
              className="w-64 h-64 md:w-80 md:h-80 object-contain"
              style={{ filter: 'drop-shadow(0 0 30px rgba(251, 133, 0, 0.8))' }}
              animate={{ rotate: [0, -5, 5, 0] }}
              transition={{ duration: 0.4, repeat: Infinity }}
            />
            {/* Cape de super-héros - seulement en mode école */}
            <motion.div
              className="absolute -z-10 -right-16 top-10"
              animate={{ scaleX: [1, 1.3, 1], x: [-5, -25, -5] }}
              transition={{ duration: 0.3, repeat: Infinity }}
            >
              <div 
                className="w-32 h-64 rounded-r-full"
                style={{ background: 'linear-gradient(90deg, #E63946 0%, transparent 100%)', opacity: 0.7 }}
              />
            </motion.div>
          </motion.div>
        )}

        {/* Stage 2: Logo école arrive et FRAPPE! (seulement si pas mode démo) */}
        {!isDemoMode && stage >= 2 && stage < 3 && (
          <>
            {/* Onomatopées GÉANTES - positionnées correctement */}
            <motion.div
              key="kaboom"
              initial={{ scale: 0, opacity: 0, rotate: -20 }}
              animate={{ scale: [0, 1.8, 1.4], opacity: 1, rotate: [-20, 5, -5] }}
              transition={{ duration: 0.5 }}
              className="absolute z-50"
              style={{ top: '5%', left: '5%' }}
            >
              <div 
                className="text-6xl md:text-8xl lg:text-9xl font-black"
                style={{
                  fontFamily: 'Bangers, cursive',
                  color: '#E63946',
                  textShadow: '4px 4px 0px #FFB703, 8px 8px 0px rgba(0,0,0,0.3)',
                  WebkitTextStroke: '2px black'
                }}
              >
                KABOOM!
              </div>
            </motion.div>

            <motion.div
              key="pow"
              initial={{ scale: 0, opacity: 0, rotate: 20 }}
              animate={{ scale: [0, 1.6, 1.2], opacity: 1, rotate: [20, -5, 5] }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="absolute z-50"
              style={{ bottom: '5%', right: '5%' }}
            >
              <div 
                className="text-5xl md:text-7xl lg:text-8xl font-black"
                style={{
                  fontFamily: 'Bangers, cursive',
                  color: '#FFB703',
                  textShadow: '4px 4px 0px #E63946, 8px 8px 0px rgba(0,0,0,0.3)',
                  WebkitTextStroke: '2px black'
                }}
              >
                POW!
              </div>
            </motion.div>

            {/* Logo école arrive en FORCE et frappe */}
            <motion.div
              key="school-logo"
              initial={{ x: 1200, y: -200, scale: 0.4, rotate: 30 }}
              animate={{ x: 0, y: 0, scale: [0.4, 1.3, 1.1], rotate: [30, -5, 0] }}
              transition={{ duration: 1, ease: [0.68, -0.55, 0.265, 1.55] }}
              className="relative z-40"
            >
              <img 
                src={school.logo}
                alt={school.name}
                className="w-60 h-60 md:w-80 md:h-80 lg:w-96 lg:h-96 object-contain"
                style={{ filter: 'drop-shadow(0 0 30px rgba(255, 107, 53, 0.8))' }}
              />
              
              {/* Effet d'impact - onde de choc */}
              <motion.div
                className="absolute inset-0 rounded-full border-4 border-white"
                initial={{ scale: 1, opacity: 1 }}
                animate={{ scale: 2.5, opacity: 0 }}
                transition={{ duration: 0.8, ease: 'easeOut' }}
              />
              <motion.div
                className="absolute inset-0 rounded-full border-4 border-yellow-400"
                initial={{ scale: 1, opacity: 1 }}
                animate={{ scale: 2, opacity: 0 }}
                transition={{ duration: 0.8, delay: 0.1, ease: 'easeOut' }}
              />
            </motion.div>
          </>
        )}

        {/* Stage 3: Messages motivants */}
        {stage >= 3 && (
          <motion.div
            key="final-message"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            className="absolute inset-0 flex flex-col items-center justify-center z-50 px-8"
          >
            <motion.h1
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="text-4xl md:text-5xl lg:text-6xl mb-6 text-center"
              style={{
                fontFamily: 'Bangers, cursive',
                color: 'white',
                textShadow: '5px 5px 0px rgba(0,0,0,0.5)',
                letterSpacing: '3px',
                lineHeight: '1.3'
              }}
            >
              DÉVELOPPE TES<br/>SUPER POUVOIRS!
            </motion.h1>
            
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-xl md:text-2xl text-center mb-3"
              style={{
                fontFamily: 'Bebas Neue, cursive',
                color: 'white',
                textShadow: '2px 2px 0px rgba(0,0,0,0.5)',
                letterSpacing: '2px'
              }}
            >
              Deviens un Super Reporter du DailyGlobe!
            </motion.p>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: [0, 1, 0.7, 1] }}
              transition={{ delay: 0.8, duration: 1.5, repeat: Infinity }}
              className="text-lg md:text-xl text-center"
              style={{
                fontFamily: 'Bebas Neue, cursive',
                color: '#FFB703',
                textShadow: '2px 2px 0px rgba(0,0,0,0.8)',
                letterSpacing: '2px'
              }}
            >
              L'aventure commence...
            </motion.p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Étoiles animées background - positions fixes */}
      {starPositions.map((pos, i) => (
        <motion.div
          key={`star-${i}`}
          className="absolute text-3xl md:text-4xl pointer-events-none"
          style={{ left: pos.left, top: pos.top, zIndex: 5 }}
          animate={{
            scale: [1, 1.4, 1],
            rotate: [0, 180, 360],
            opacity: [0.4, 0.8, 0.4]
          }}
          transition={{
            duration: 2.5,
            repeat: Infinity,
            delay: i * 0.2
          }}
        >
          ⭐
        </motion.div>
      ))}

      <style jsx>{`
        @keyframes shake {
          0%, 100% { transform: translate(0, 0); }
          10% { transform: translate(-10px, -10px); }
          20% { transform: translate(10px, 10px); }
          30% { transform: translate(-10px, 10px); }
          40% { transform: translate(10px, -10px); }
          50% { transform: translate(-8px, -8px); }
          60% { transform: translate(8px, 8px); }
          70% { transform: translate(-6px, 6px); }
          80% { transform: translate(6px, -6px); }
          90% { transform: translate(-3px, 3px); }
        }
        .animate-shake {
          animation: shake 0.5s ease-in-out;
        }
      `}</style>
    </div>
  );
}

export default SplashScreen;
